/**
 * Derive package facts from a dancer enrollment record.
 * Room type is the source of truth for Ballet Retreat vs Festival Access.
 */

import { freeGiftCountFromYearsAttended } from '@/config/iabf-gifts'
import { hasCoffeeMakerPicks } from '@/config/iabf-dietary'

const GIFT_IGNORE = ['None', '', null, undefined]

const ONSITE_OWN_ROOM = ['Standard Dorm', 'ADA Dorm', 'Cottage']

/**
 * @param {string} room
 * @returns {'retreat' | 'access' | ''}
 */
export function packageKeyFromRoom (room) {
  if (!room) {
    return ''
  }
  if (ONSITE_OWN_ROOM.includes(room)) {
    return 'retreat'
  }
  // Offsite tickets and sharing a room with a retreat roommate → Access Pass
  if (
    room === 'Staying with another dancer' ||
    room === 'Staying offsite' ||
    String(room).indexOf('offsite') !== -1
  ) {
    return 'access'
  }
  return ''
}

/**
 * Marketing package name from pricing table.
 * @param {string} room
 * @returns {string}
 */
export function packageNameFromRoom (room) {
  const key = packageKeyFromRoom(room)
  if (key === 'retreat') {
    return 'Ballet Retreat Package'
  }
  if (key === 'access') {
    return 'Festival Access Pass'
  }
  return 'IABF Festival registration'
}

/**
 * Short lodging line for cards and travel page.
 * @param {string} room
 * @returns {string}
 */
export function lodgingLabelFromRoom (room) {
  if (!room) {
    return 'Room coming soon'
  }
  if (room === 'Standard Dorm') {
    return 'Your own onsite dorm room'
  }
  if (room === 'ADA Dorm') {
    return 'Your own larger onsite dorm room'
  }
  if (room === 'Cottage') {
    return 'Onsite cottage'
  }
  if (room === 'Staying with another dancer') {
    return 'Onsite roommate (sharing a room)'
  }
  if (
    room === 'Staying offsite' ||
    room === 'Staying offsite (shuttle)' ||
    room === 'Staying offsite (no shuttle)' ||
    String(room).indexOf('offsite') !== -1
  ) {
    return 'Staying offsite'
  }
  return room
}

const ONSITE_PREP_ROOMS = ONSITE_OWN_ROOM.concat(['Staying with another dancer'])

export function isOnsiteLodgingRoom (room) {
  return ONSITE_PREP_ROOMS.includes(room)
}

/** Physical onsite room the person booked (not sharing someone else’s). */
export function isOnsiteOwnRoom (room) {
  return ONSITE_OWN_ROOM.indexOf(String(room || '').trim()) !== -1
}

export function isOffsiteLodgingRoom (room) {
  return String(room || '').toLowerCase().indexOf('offsite') !== -1
}

/** Anyone who belongs on the admin rooming list (owns or shares an onsite bed). */
export function isOnsiteForRooming (room) {
  return !!(room && !isOffsiteLodgingRoom(room))
}

export function normalizeRoomNumber (v) {
  return String(v == null ? '' : v).trim()
}

export function assignedRoomNumber (dancer) {
  const d = dancer || {}
  if (isOffsiteLodgingRoom(d.room)) {
    return ''
  }
  return normalizeRoomNumber(d.roomNumber)
}

export function lodgingLineWithRoomNumber (room, roomNumber) {
  const label = lodgingLabelFromRoom(room)
  const n = normalizeRoomNumber(roomNumber)
  if (!n || isOffsiteLodgingRoom(room)) {
    return label
  }
  return label + ' · Room ' + n
}

/**
 * Trip itinerary is on file (enrollment flag, dancer fields, or travel doc).
 * Extra nights alone do not count.
 */
export function hasTripDetailsOnFile (dancer, travelDoc) {
  const d = dancer || {}
  if (d.travelDetailsEntered === true) {
    return true
  }
  if (d.arrivalType || d.departureType) {
    return true
  }
  const t = travelDoc || {}
  return !!(
    t.arrivalType ||
    t.departureType ||
    t.arrivalAirport ||
    t.departureAirport
  )
}

/**
 * Required lodging prep for the dashboard “Getting here” card.
 * Extra nights and carpool are optional and must not keep this as to-do.
 */
export function lodgingRequiredPrepDone (dancer, travelDoc) {
  const d = dancer || {}
  if (!hasTripDetailsOnFile(d, travelDoc)) {
    return false
  }
  if (d.isFaculty || d.personType === 'faculty') {
    return true
  }
  if (!isOnsiteLodgingRoom(d.room)) {
    return true
  }
  if (d.wantsHairdryer !== true && d.wantsHairdryer !== false) {
    return false
  }
  if (!hasCoffeeMakerPicks(d.coffeeMakerStock, d.coffee)) {
    return false
  }
  return true
}

/**
 * Active registration gifts (excludes None / empty / not selected).
 * Supports up to 4 returning-dancer gifts; early-bird is legacy only (no 2027 early bird).
 * @param {object} dancer
 * @returns {{ earlyBird: string|null, gifts: string[], needsSelection: boolean }}
 */
export function giftsFromDancer (dancer) {
  const d = dancer || {}
  const earlyRaw = d.earlyBirdGift
  const earlyBird = earlyRaw && !GIFT_IGNORE.includes(earlyRaw) && earlyRaw !== 'Not Yet Selected'
    ? earlyRaw
    : null
  const raw = [
    d.registrationGift1,
    d.registrationGift2,
    d.registrationGift3,
    d.registrationGift4
  ]
  const gifts = raw.filter(g => g && !GIFT_IGNORE.includes(g) && g !== 'Not Yet Selected')
  // Returning dancers earn gifts even if enrollment still has default "None".
  const entitled = freeGiftCountFromYearsAttended(
    d.yearsAttended,
    d.festivalYear
  )
  const needsSelection = entitled > gifts.length
  return { earlyBird, gifts, needsSelection, entitled }
}

/**
 * @typedef {{ label: string, to?: string, cta?: string }} AmenityItem
 */

/**
 * Coffee maker line — stocked only when they’ve actually picked supplies.
 * @param {{ coffeeMakerStock?: string[], coffee?: string }} [opts]
 * @returns {AmenityItem}
 */
function coffeeMakerAmenity (opts = {}) {
  if (hasCoffeeMakerPicks(opts.coffeeMakerStock, opts.coffee)) {
    return {
      label: 'Coffee maker — stocked to your preferences'
    }
  }
  return {
    label: 'Coffee maker',
    to: '/lodging',
    cta: 'choose what to stock'
  }
}

/**
 * Hairdryer is optional — dancers choose on Travel & lodging (onsite / roommate only).
 * @param {boolean|null|undefined} wantsHairdryer
 * @returns {AmenityItem}
 */
function hairdryerAmenity (wantsHairdryer) {
  if (wantsHairdryer === true) {
    return { label: 'Hairdryer — yes, one in the room' }
  }
  if (wantsHairdryer === false) {
    return { label: 'Hairdryer — not needed' }
  }
  return {
    label: 'Hairdryer',
    to: '/lodging',
    cta: 'choose yes or no'
  }
}

/**
 * Shared bath/stock amenities for onsite rooms.
 * @param {{ dietaryEntered?: boolean, wantsHairdryer?: boolean|null, coffeeMakerStock?: string[], coffee?: string }} opts
 * @returns {AmenityItem[]}
 */
function baseOnsiteBathAndStock (opts) {
  return [
    { label: 'Private bathroom & shower' },
    hairdryerAmenity(opts.wantsHairdryer),
    { label: 'Linens & towels' },
    { label: 'Shampoo, conditioner, bodywash & lotion' },
    coffeeMakerAmenity(opts),
    { label: 'Mini fridge' },
    { label: 'Microwave' }
  ]
}

/**
 * In-room amenities for onsite stays (shared by registration + lodging pages).
 * @param {string} room
 * @param {{ dietaryEntered?: boolean, wantsHairdryer?: boolean|null, coffeeMakerStock?: string[], coffee?: string }} [opts]
 * @returns {{ title: string, items: AmenityItem[] } | null}
 */
export function roomAmenitiesForRoom (room, opts = {}) {
  const wantsHairdryer = opts.wantsHairdryer
  if (room === 'Standard Dorm') {
    return {
      title: 'In your room',
      items: [
        { label: 'Double bed' },
        ...baseOnsiteBathAndStock(opts)
      ]
    }
  }
  if (room === 'ADA Dorm') {
    return {
      title: 'In your room',
      items: [
        { label: 'Double bed' },
        { label: 'Single pull-out bed' },
        ...baseOnsiteBathAndStock(opts)
      ]
    }
  }
  if (room === 'Cottage') {
    return {
      title: 'In your cottage',
      items: [
        { label: 'Double bed' },
        { label: 'Pull-out couch (double bed)' },
        { label: 'Private bathroom & shower' },
        hairdryerAmenity(wantsHairdryer),
        { label: 'Linens & towels' },
        { label: 'Shampoo, conditioner, bodywash & lotion' },
        coffeeMakerAmenity(opts),
        { label: 'Full-size refrigerator' },
        { label: 'Mini kitchen' },
        { label: 'Microwave' }
      ]
    }
  }
  if (room === 'Staying with another dancer') {
    return {
      title: 'In the shared room',
      items: [
        { label: 'Private bathroom & shower' },
        hairdryerAmenity(wantsHairdryer),
        { label: 'Linens & towels' },
        { label: 'Bath products (shampoo, conditioner, bodywash & lotion)' },
        coffeeMakerAmenity(opts),
        { label: 'Mini fridge' },
        { label: 'Microwave' }
      ]
    }
  }
  return null
}

/** Flatten amenity items for plain-text / registration list detail. */
export function amenityItemsToDetail (items) {
  return (items || []).map(item => {
    if (typeof item === 'string') {
      return item
    }
    return item.label
  }).join(' · ')
}

/**
 * Grouped “what’s included” for the registration page.
 * @param {object} dancer
 * @returns {{ title: string, items: { included: boolean, label: string, detail?: string }[] }[]}
 */
export function registrationIncludesGrouped (dancer) {
  const d = dancer || {}
  const room = d.room || ''
  const key = packageKeyFromRoom(room)
  const lodging = lodgingLabelFromRoom(room)
  const { gifts, needsSelection } = giftsFromDancer(d)
  const onsiteOwnRoom = key === 'retreat'
  const onsiteRoommate = room === 'Staying with another dancer'
  const offsiteShuttle = room === 'Staying offsite (shuttle)' || room === 'Staying offsite'
  const offsiteNoShuttle = room === 'Staying offsite (no shuttle)'

  const groups = [
    {
      title: 'Classes & coaching',
      items: [
        {
          included: true,
          label: 'Ballet technique classes',
          detail: 'Beginner and Intermediate levels available. You don’t need to choose ahead of time. Simply drop into any class during IABF.'
        },
        {
          included: true,
          label: 'Jazz & Contemporary classes',
          detail: 'Ballet classes are available all day, but if you’d like to branch out of your comfort zone, hop into a Jazz or Contemporary class. No experience required.'
        },
        {
          included: true,
          label: 'Small-group breakout sessions',
          detail: 'Deep dive into topics like turnout, flexibility, conditioning, pre-pointe, pantomime, and more.'
        },
        {
          included: true,
          label: 'Stage makeup video training',
          detail: 'Watch anytime from your dashboard'
        }
      ]
    },
    {
      title: 'Perform & compete',
      items: [
        {
          included: true,
          label: '5½ minutes on stage',
          detail: 'Up to 3 solos, plus small groups · each piece max 3:15'
        },
        {
          included: true,
          label: 'Dance in the Corps de Ballet',
          detail: '1–2 pieces, space permitting · tutu rental or purchase available · you provide leotard, shoes & tights'
        },
        {
          included: true,
          label: '1 competition entry',
          detail: 'Optional · receive feedback from our judges & win prizes'
        },
        {
          included: true,
          label: 'Dress rehearsal & show day',
          detail: 'Full performance experience'
        },
        {
          included: true,
          label: 'Showcase video recording',
          detail: 'Your performances after the festival'
        },
        {
          included: true,
          label: 'Professional onstage photography',
          detail: 'Beautiful shots of you in performance'
        }
      ]
    },
    {
      title: 'Meals & hospitality',
      items: [
        {
          included: true,
          label: 'Breakfast, lunch & dinner',
          detail: 'Plus Welcome Wine, Awards Banquet + Cocktail Party'
        },
        {
          included: true,
          label: 'Snacks, water refills & leftovers',
          detail: 'Between meals so you stay fueled'
        },
        {
          included: true,
          label: 'Evening activities',
          detail: 'Social and festival programming after classes'
        }
      ]
    }
  ]

  // Stay / lodging group (package-specific)
  const stayItems = []
  if (onsiteOwnRoom) {
    stayItems.push({
      included: true,
      label: 'On-site room',
      detail: lodging
    })
    const amenities = roomAmenitiesForRoom(room, {
      dietaryEntered: !!d.dietaryRestrictionsEntered,
      wantsHairdryer: d.wantsHairdryer,
      coffeeMakerStock: d.coffeeMakerStock,
      coffee: d.coffee
    })
    if (amenities) {
      stayItems.push({
        included: true,
        label: amenities.title,
        detail: amenityItemsToDetail(amenities.items)
      })
    }
  } else if (onsiteRoommate) {
    const shareName = String(d.roommateName || d.primaryRoommateName || '').trim()
    stayItems.push({
      included: true,
      label: shareName ? `Staying with ${shareName}` : 'Staying on site with a roommate',
      detail: shareName
        ? 'You’re sharing their onsite room'
        : 'You’re sharing with someone who has an onsite room'
    })
    const amenities = roomAmenitiesForRoom(room, {
      dietaryEntered: !!d.dietaryRestrictionsEntered,
      wantsHairdryer: d.wantsHairdryer,
      coffeeMakerStock: d.coffeeMakerStock,
      coffee: d.coffee
    })
    if (amenities) {
      stayItems.push({
        included: true,
        label: amenities.title,
        detail: amenityItemsToDetail(amenities.items)
      })
    }
  } else if (offsiteShuttle) {
    stayItems.push({
      included: true,
      label: 'Free shuttle to SpringHill Suites New Smyrna Beach',
      detail: 'Back and forth to the beach hotel'
    })
  } else if (offsiteNoShuttle || key === 'access') {
    if (offsiteNoShuttle) {
      stayItems.push({
        included: false,
        label: 'Beach hotel shuttle',
        detail: 'Not included on the no-shuttle offsite option'
      })
    }
  } else if (room) {
    stayItems.push({
      included: true,
      label: 'Your stay',
      detail: lodging
    })
  }

  if (stayItems.length) {
    groups.push({
      title: 'Your stay',
      items: stayItems
    })
  }

  groups.push({
    title: 'Extras',
    items: [
      {
        included: true,
        label: 'Free IABF t-shirt',
        detail: 'Festival merch for every dancer'
      },
      {
        included: true,
        label: 'Professional candid photography',
        detail: 'Throughout the event'
      }
    ]
  })

  if (gifts.length) {
    groups.push({
      title: 'Thank-you gifts',
      items: [
        {
          included: true,
          label: gifts.length === 1 ? 'A free gift for returning' : 'Free gifts for returning',
          detail: gifts.join(' · ')
        }
      ]
    })
  } else if (needsSelection) {
    groups.push({
      title: 'Thank-you gifts',
      items: [
        {
          included: true,
          label: 'Free gifts for returning',
          detail: 'Still choosing? Pick them below — or email hello@iabf.dance'
        }
      ]
    })
  }

  return groups
}

/**
 * Flat checklist (legacy / callers that don’t need groups).
 * @param {object} dancer
 * @returns {{ included: boolean, label: string, detail?: string }[]}
 */
export function registrationIncludes (dancer) {
  return registrationIncludesGrouped(dancer).reduce((acc, g) => {
    return acc.concat(g.items || [])
  }, [])
}

/**
 * One-line summary for dashboard cards.
 * @param {object} dancer
 * @returns {string}
 */
export function registrationSummaryLine (dancer) {
  const d = dancer || {}
  const packageName = packageNameFromRoom(d.room)
  const lodging = lodgingLabelFromRoom(d.room)
  const { gifts, earlyBird, needsSelection, entitled } = giftsFromDancer(d)
  const giftBit = needsSelection
    ? (entitled > 1 ? 'gifts still to pick' : 'gift still to pick')
    : (gifts.length || earlyBird)
      ? (gifts.length + (earlyBird ? 1 : 0) > 1 ? 'gifts included' : 'a gift for you')
      : null
  const parts = [packageName]
  if (d.room) {
    parts.push(lodging)
  }
  parts.push('5½ min solo time')
  if (giftBit) {
    parts.push(giftBit)
  }
  return parts.join(' · ')
}
